import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';

import engineApi from '@/shared/services/engineApi';
import logger from '@/shared/utils/logger';
import SEO from '@/components/common/SEO';

import DataSourceStatusBadge from './components/DataSourceStatusBadge';

const PRODUCT_TYPES = [
    'storymap',
    'dashboard',
    'report',
    'visualization',
    'database',
    'map_viewer',
    'other',
];

const API_TYPES = ['arcgis_rest', 'soda', 'custom', 'other'];

const EMPTY_PRODUCT = {
    name: '',
    url: '',
    description: '',
    product_type: '',
    spotlight: 'water',
    organization: '',
    category: '',
    keywords: '',
};

const EMPTY_SOURCE = {
    name: '',
    base_url: '',
    description: '',
    api_type: 'other',
    url_template: '',
    spotlight: 'water',
    organization: '',
    category: '',
    keywords: '',
    scale_extent: '',
    update_frequency: '',
    data_origin: '',
};

export default function DataSourceDashboard() {
    const [healthStats, setHealthStats] = useState(null);
    const [loadingHealth, setLoadingHealth] = useState(true);
    const [validatingAll, setValidatingAll] = useState(false);
    const [indexingAll, setIndexingAll] = useState(false);
    const [checkingEmbeddability, setCheckingEmbeddability] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Modal state
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [showAddSource, setShowAddSource] = useState(false);
    const [formData, setFormData] = useState(EMPTY_PRODUCT);
    const [submitting, setSubmitting] = useState(false);

    const fetchHealth = useCallback(async () => {
        try {
            const data = await engineApi.get('/admin/data-health');
            setHealthStats(data);
        } catch (err) {
            logger.error('Failed to fetch health stats:', err);
            setError('Failed to load health statistics');
        } finally {
            setLoadingHealth(false);
        }
    }, []);

    useEffect(() => {
        fetchHealth();
    }, [fetchHealth]);

    // --- Bulk operations ---

    const handleValidateAll = useCallback(async () => {
        setValidatingAll(true);
        setError(null);
        try {
            const result = await engineApi.post('/admin/validate-all');
            await fetchHealth();
            const summary = result.summary || {};
            setSuccessMsg(
                `Validated ${summary.sources_validated || 0} sources and ${summary.products_validated || 0} products`
            );
            setTimeout(() => setSuccessMsg(null), 8000);
        } catch (err) {
            logger.error('Bulk validation failed:', err);
            setError('Bulk validation failed');
        } finally {
            setValidatingAll(false);
        }
    }, [fetchHealth]);

    const handleIndexAll = useCallback(async () => {
        setIndexingAll(true);
        setError(null);
        try {
            const result = await engineApi.post('/admin/index-all');
            await fetchHealth();
            setSuccessMsg(
                result.message ||
                    `Indexed ${result.indexed || 0} records into OpenSearch`
            );
            setTimeout(() => setSuccessMsg(null), 8000);
        } catch (err) {
            logger.error('Bulk indexing failed:', err);
            setError(err.message || 'Bulk indexing failed');
        } finally {
            setIndexingAll(false);
        }
    }, [fetchHealth]);

    const handleCheckEmbeddability = useCallback(async () => {
        setCheckingEmbeddability(true);
        setError(null);
        try {
            const result = await engineApi.post('/admin/check-embeddability');
            await fetchHealth();
            const s = result.summary || {};
            setSuccessMsg(
                `Checked ${s.products_checked || 0} products (${s.products_embeddable || 0} embeddable) and ${s.sources_checked || 0} sources (${s.sources_embeddable || 0} embeddable)`
            );
            setTimeout(() => setSuccessMsg(null), 8000);
        } catch (err) {
            logger.error('Embeddability check failed:', err);
            setError('Embeddability check failed');
        } finally {
            setCheckingEmbeddability(false);
        }
    }, [fetchHealth]);

    // --- Create handlers ---

    const handleFormChange = useCallback(
        (field) => (e) => {
            setFormData((prev) => ({ ...prev, [field]: e.target.value }));
        },
        []
    );

    const handleOpenAddProduct = useCallback(() => {
        setFormData(EMPTY_PRODUCT);
        setShowAddProduct(true);
    }, []);

    const handleOpenAddSource = useCallback(() => {
        setFormData(EMPTY_SOURCE);
        setShowAddSource(true);
    }, []);

    const handleSubmitProduct = useCallback(
        async (e) => {
            e.preventDefault();
            setSubmitting(true);
            setError(null);
            try {
                const payload = {
                    name: formData.name,
                    url: formData.url,
                    description: formData.description || undefined,
                    product_type: formData.product_type || undefined,
                    spotlight: formData.spotlight || undefined,
                    organization: formData.organization || undefined,
                    category: formData.category
                        ? formData.category.split(',').map((s) => s.trim())
                        : undefined,
                    keywords: formData.keywords
                        ? formData.keywords.split(',').map((s) => s.trim())
                        : undefined,
                };
                const result = await engineApi.post('/admin/products', payload);

                setShowAddProduct(false);
                fetchHealth();

                const msg = result.indexed
                    ? `Product "${result.record.name}" created and indexed (${result.chunks} chunks)`
                    : `Product "${result.record.name}" created`;
                setSuccessMsg(
                    result.warning ? `${msg}. ${result.warning}` : msg
                );
                setTimeout(() => setSuccessMsg(null), 8000);
            } catch (err) {
                logger.error('Failed to create product:', err);
                setError(err.message || 'Failed to create product');
            } finally {
                setSubmitting(false);
            }
        },
        [formData, fetchHealth]
    );

    const handleSubmitSource = useCallback(
        async (e) => {
            e.preventDefault();
            setSubmitting(true);
            setError(null);
            try {
                const payload = {
                    name: formData.name,
                    base_url: formData.base_url,
                    description: formData.description || undefined,
                    api_type: formData.api_type || undefined,
                    url_template: formData.url_template || undefined,
                    spotlight: formData.spotlight || undefined,
                    organization: formData.organization || undefined,
                    scale_extent: formData.scale_extent || undefined,
                    update_frequency: formData.update_frequency || undefined,
                    data_origin: formData.data_origin || undefined,
                    category: formData.category
                        ? formData.category.split(',').map((s) => s.trim())
                        : undefined,
                    keywords: formData.keywords
                        ? formData.keywords.split(',').map((s) => s.trim())
                        : undefined,
                };
                const result = await engineApi.post('/admin/sources', payload);

                setShowAddSource(false);
                fetchHealth();

                const msg = result.indexed
                    ? `Source "${result.record.name}" created and indexed`
                    : `Source "${result.record.name}" created`;
                setSuccessMsg(
                    result.warning ? `${msg}. ${result.warning}` : msg
                );
                setTimeout(() => setSuccessMsg(null), 8000);
            } catch (err) {
                logger.error('Failed to create source:', err);
                setError(err.message || 'Failed to create data source');
            } finally {
                setSubmitting(false);
            }
        },
        [formData, fetchHealth]
    );

    // Derived stats for smart links
    const sourceStats = healthStats?.data_sources || {};
    const productStats = healthStats?.data_products || {};

    return (
        <section
            id="admin-data-sources"
            className="page-container"
            aria-labelledby="admin-data-sources-heading"
        >
            <SEO title="Admin: Data Sources" />
            <Container className="py-4">
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <h2
                            id="admin-data-sources-heading"
                            className="fw-bold text-white"
                        >
                            Data Admin Hub
                        </h2>
                        <p className="text-white mb-0">
                            Health overview, bulk operations, and quick links
                            into{' '}
                            <Link to="/data" className="text-white">
                                Data Discovery
                            </Link>
                            .
                        </p>
                    </Col>
                </Row>

                {error && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {successMsg && (
                    <Alert
                        variant="success"
                        dismissible
                        onClose={() => setSuccessMsg(null)}
                    >
                        {successMsg}
                    </Alert>
                )}

                {/* Health Stats */}
                {loadingHealth ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    healthStats && (
                        <>
                            <Row className="g-3 mb-4">
                                <Col sm={6} lg={3}>
                                    <Link
                                        to="/data?tab=sources"
                                        className="text-decoration-none"
                                    >
                                        <Card className="border-0 shadow-sm text-center p-3">
                                            <div className="fs-3 fw-bold text-primary">
                                                {sourceStats.total}
                                            </div>
                                            <small className="text-muted">
                                                Data Sources
                                            </small>
                                        </Card>
                                    </Link>
                                </Col>
                                <Col sm={6} lg={3}>
                                    <Link
                                        to="/data?tab=products"
                                        className="text-decoration-none"
                                    >
                                        <Card className="border-0 shadow-sm text-center p-3">
                                            <div className="fs-3 fw-bold text-primary">
                                                {productStats.total}
                                            </div>
                                            <small className="text-muted">
                                                Data Products
                                            </small>
                                        </Card>
                                    </Link>
                                </Col>
                                <Col sm={6} lg={3}>
                                    <Link
                                        to="/data?status=active"
                                        className="text-decoration-none"
                                    >
                                        <Card className="border-0 shadow-sm text-center p-3">
                                            <div className="fs-3 fw-bold text-success">
                                                {sourceStats.active +
                                                    productStats.active}
                                            </div>
                                            <small className="text-muted">
                                                Active
                                            </small>
                                        </Card>
                                    </Link>
                                </Col>
                                <Col sm={6} lg={3}>
                                    <Link
                                        to="/data?status=error"
                                        className="text-decoration-none"
                                    >
                                        <Card className="border-0 shadow-sm text-center p-3">
                                            <div className="fs-3 fw-bold text-danger">
                                                {sourceStats.errors +
                                                    productStats.errors}
                                            </div>
                                            <small className="text-muted">
                                                Errors
                                            </small>
                                        </Card>
                                    </Link>
                                </Col>
                            </Row>

                            {/* Smart Links */}
                            <h3 className="h5 text-white fw-semibold mb-3">
                                Quick Filters
                            </h3>
                            <div className="d-flex flex-wrap gap-3 mb-4">
                                {sourceStats.errors > 0 && (
                                    <Link
                                        to="/data?tab=sources&status=error"
                                        className="text-decoration-none flex-fill"
                                    >
                                        <Card className="border-0 shadow-sm h-100 border-start border-danger border-3">
                                            <Card.Body className="d-flex align-items-center gap-2">
                                                <DataSourceStatusBadge status="error" />
                                                <span className="text-body">
                                                    {sourceStats.errors} error
                                                    source
                                                    {sourceStats.errors !== 1 &&
                                                        's'}
                                                </span>
                                            </Card.Body>
                                        </Card>
                                    </Link>
                                )}
                                {productStats.errors > 0 && (
                                    <Link
                                        to="/data?tab=products&status=error"
                                        className="text-decoration-none flex-fill"
                                    >
                                        <Card className="border-0 shadow-sm h-100 border-start border-danger border-3">
                                            <Card.Body className="d-flex align-items-center gap-2">
                                                <DataSourceStatusBadge status="error" />
                                                <span className="text-body">
                                                    {productStats.errors} error
                                                    product
                                                    {productStats.errors !==
                                                        1 && 's'}
                                                </span>
                                            </Card.Body>
                                        </Card>
                                    </Link>
                                )}
                                {sourceStats.deprecated > 0 && (
                                    <Link
                                        to="/data?tab=sources&status=deprecated"
                                        className="text-decoration-none flex-fill"
                                    >
                                        <Card className="border-0 shadow-sm h-100 border-start border-dark border-3">
                                            <Card.Body className="d-flex align-items-center gap-2">
                                                <DataSourceStatusBadge status="deprecated" />
                                                <span className="text-body">
                                                    {sourceStats.deprecated}{' '}
                                                    deprecated
                                                </span>
                                            </Card.Body>
                                        </Card>
                                    </Link>
                                )}
                                {sourceStats.pending > 0 && (
                                    <Link
                                        to="/data?tab=sources&status=pending"
                                        className="text-decoration-none flex-fill"
                                    >
                                        <Card className="border-0 shadow-sm h-100 border-start border-warning border-3">
                                            <Card.Body className="d-flex align-items-center gap-2">
                                                <DataSourceStatusBadge status="pending" />
                                                <span className="text-body">
                                                    {sourceStats.pending}{' '}
                                                    pending/new
                                                </span>
                                            </Card.Body>
                                        </Card>
                                    </Link>
                                )}
                                <Link
                                    to="/data"
                                    className="text-decoration-none flex-fill"
                                >
                                    <Card className="border-0 shadow-sm h-100 border-start border-primary border-3">
                                        <Card.Body className="d-flex align-items-center gap-2">
                                            <span className="text-primary fw-medium">
                                                Browse all data
                                            </span>
                                        </Card.Body>
                                    </Card>
                                </Link>
                            </div>
                        </>
                    )
                )}

                {/* Actions */}
                <h3 className="h5 text-white fw-semibold mb-3">Actions</h3>
                <Row className="g-3 mb-4">
                    {/* Bulk Operations */}
                    <Col sm={6} lg={4}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body>
                                <Card.Title className="h6 mb-3">
                                    Bulk Operations
                                </Card.Title>
                                <div className="d-flex flex-column gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleValidateAll}
                                        disabled={
                                            validatingAll ||
                                            indexingAll ||
                                            checkingEmbeddability
                                        }
                                    >
                                        {validatingAll ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                Validating...
                                            </>
                                        ) : (
                                            'Validate All URLs'
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        onClick={handleCheckEmbeddability}
                                        disabled={
                                            validatingAll ||
                                            indexingAll ||
                                            checkingEmbeddability
                                        }
                                    >
                                        {checkingEmbeddability ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                Checking...
                                            </>
                                        ) : (
                                            'Check All Embeddability'
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        onClick={handleIndexAll}
                                        disabled={
                                            validatingAll ||
                                            indexingAll ||
                                            checkingEmbeddability
                                        }
                                    >
                                        {indexingAll ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                Indexing...
                                            </>
                                        ) : (
                                            'Re-index All'
                                        )}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Create Data Source */}
                    <Col sm={6} lg={4}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body>
                                <Card.Title className="h6 mb-3">
                                    Add Data Source
                                </Card.Title>
                                <p className="small text-muted mb-3">
                                    Register a new API or dataset endpoint. It
                                    will be automatically indexed for AI search.
                                </p>
                                <Button
                                    variant="outline-primary"
                                    onClick={handleOpenAddSource}
                                >
                                    + Add Source
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Create Data Product */}
                    <Col sm={6} lg={4}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body>
                                <Card.Title className="h6 mb-3">
                                    Add Data Product
                                </Card.Title>
                                <p className="small text-muted mb-3">
                                    Register a report, storymap, dashboard, or
                                    other visualization. Content will be scraped
                                    and indexed.
                                </p>
                                <Button
                                    variant="outline-primary"
                                    onClick={handleOpenAddProduct}
                                >
                                    + Add Product
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Add Product Modal */}
                <Modal
                    show={showAddProduct}
                    onHide={() => setShowAddProduct(false)}
                    size="lg"
                    aria-labelledby="add-product-title"
                >
                    <Form onSubmit={handleSubmitProduct}>
                        <Modal.Header closeButton>
                            <Modal.Title
                                as="h2"
                                className="h4"
                                id="add-product-title"
                            >
                                Add Data Product
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Row className="g-3">
                                <Col md={8}>
                                    <Form.Group controlId="product-name">
                                        <Form.Label>
                                            Name{' '}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            value={formData.name}
                                            onChange={handleFormChange('name')}
                                            placeholder="e.g., Colorado Water Atlas StoryMap"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="product-type">
                                        <Form.Label>Product Type</Form.Label>
                                        <Form.Select
                                            value={formData.product_type}
                                            onChange={handleFormChange(
                                                'product_type'
                                            )}
                                        >
                                            <option value="">
                                                Select type
                                            </option>
                                            {PRODUCT_TYPES.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group controlId="product-url">
                                        <Form.Label>
                                            URL{' '}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            type="url"
                                            value={formData.url}
                                            onChange={handleFormChange('url')}
                                            placeholder="https://storymaps.arcgis.com/stories/..."
                                        />
                                        <Form.Text className="text-muted">
                                            Content will be automatically
                                            scraped and indexed for AI search
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group controlId="product-description">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={formData.description}
                                            onChange={handleFormChange(
                                                'description'
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="product-spotlight">
                                        <Form.Label>Spotlight</Form.Label>
                                        <Form.Control
                                            value={formData.spotlight}
                                            onChange={handleFormChange(
                                                'spotlight'
                                            )}
                                            placeholder="e.g., water"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="product-organization">
                                        <Form.Label>Organization</Form.Label>
                                        <Form.Control
                                            value={formData.organization}
                                            onChange={handleFormChange(
                                                'organization'
                                            )}
                                            placeholder="e.g., CWCB"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="product-category">
                                        <Form.Label>Categories</Form.Label>
                                        <Form.Control
                                            value={formData.category}
                                            onChange={handleFormChange(
                                                'category'
                                            )}
                                            placeholder="water, drought (comma-separated)"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="product-keywords">
                                        <Form.Label>Keywords</Form.Label>
                                        <Form.Control
                                            value={formData.keywords}
                                            onChange={handleFormChange(
                                                'keywords'
                                            )}
                                            placeholder="colorado, atlas (comma-separated)"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => setShowAddProduct(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        Creating...
                                    </>
                                ) : (
                                    'Create & Index'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Add Source Modal */}
                <Modal
                    show={showAddSource}
                    onHide={() => setShowAddSource(false)}
                    size="lg"
                    aria-labelledby="add-source-title"
                >
                    <Form onSubmit={handleSubmitSource}>
                        <Modal.Header closeButton>
                            <Modal.Title
                                as="h2"
                                className="h4"
                                id="add-source-title"
                            >
                                Add Data Source
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Row className="g-3">
                                <Col md={8}>
                                    <Form.Group controlId="source-name">
                                        <Form.Label>
                                            Name{' '}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            value={formData.name}
                                            onChange={handleFormChange('name')}
                                            placeholder="e.g., Colorado DWR Water Rights"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-api-type">
                                        <Form.Label>API Type</Form.Label>
                                        <Form.Select
                                            value={formData.api_type}
                                            onChange={handleFormChange(
                                                'api_type'
                                            )}
                                        >
                                            {API_TYPES.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group controlId="source-base-url">
                                        <Form.Label>
                                            Base URL{' '}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            type="url"
                                            value={formData.base_url}
                                            onChange={handleFormChange(
                                                'base_url'
                                            )}
                                            placeholder="https://data.colorado.gov/resource/..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group controlId="source-url-template">
                                        <Form.Label>URL Template</Form.Label>
                                        <Form.Control
                                            value={formData.url_template}
                                            onChange={handleFormChange(
                                                'url_template'
                                            )}
                                            placeholder="Parameterized query template (optional)"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group controlId="source-description">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={formData.description}
                                            onChange={handleFormChange(
                                                'description'
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-spotlight">
                                        <Form.Label>Spotlight</Form.Label>
                                        <Form.Control
                                            value={formData.spotlight}
                                            onChange={handleFormChange(
                                                'spotlight'
                                            )}
                                            placeholder="e.g., water"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-organization">
                                        <Form.Label>Organization</Form.Label>
                                        <Form.Control
                                            value={formData.organization}
                                            onChange={handleFormChange(
                                                'organization'
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-data-origin">
                                        <Form.Label>Data Origin</Form.Label>
                                        <Form.Control
                                            value={formData.data_origin}
                                            onChange={handleFormChange(
                                                'data_origin'
                                            )}
                                            placeholder="e.g., Federal, State"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-scale-extent">
                                        <Form.Label>Scale/Extent</Form.Label>
                                        <Form.Control
                                            value={formData.scale_extent}
                                            onChange={handleFormChange(
                                                'scale_extent'
                                            )}
                                            placeholder="e.g., State, County"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-update-frequency">
                                        <Form.Label>
                                            Update Frequency
                                        </Form.Label>
                                        <Form.Control
                                            value={formData.update_frequency}
                                            onChange={handleFormChange(
                                                'update_frequency'
                                            )}
                                            placeholder="e.g., Daily, Monthly"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="source-category">
                                        <Form.Label>Categories</Form.Label>
                                        <Form.Control
                                            value={formData.category}
                                            onChange={handleFormChange(
                                                'category'
                                            )}
                                            placeholder="comma-separated"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group controlId="source-keywords">
                                        <Form.Label>Keywords</Form.Label>
                                        <Form.Control
                                            value={formData.keywords}
                                            onChange={handleFormChange(
                                                'keywords'
                                            )}
                                            placeholder="comma-separated"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => setShowAddSource(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        Creating...
                                    </>
                                ) : (
                                    'Create & Index'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </section>
    );
}
